import { Sites } from '@ps/platform-message-framework/common/Sites';
import type { Reference, SiteHeader } from '@ps/web-martech-cms-types';
import type {
  MainNavigationBlock,
  SecondaryNavigationBlock,
  SupportNavigationBlock
} from '@ps/web-martech-cms-types/SiteHeader';
import { DismissIcon, StarsButton, useBodyScrollLock } from '@ps/web-shared-components';
import type { ClassNameProp } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

import useCustomHeader from '../../../../hooks/useCustomHeader';
import useSeoMenu from '../../../../hooks/useSeoMenu';
import BrandIconButton from '../BrandIcon/BrandIconButton';
import BrandLinks from '../BrandLinks/BrandLinks';
import CMSHeaderMenu from '../CMSHeaderMenu/CMSHeaderMenu';
import MoreSection from '../MoreSection/MoreSection';
import SEOMenu from '../SEOMenu/SEOMenu';

import styles from './Sidebar.scss';

type MobileMenuProps = {
  toggleMenu: () => void;
  menuOpen: boolean;
  main: MainNavigationBlock[];
  primary: SupportNavigationBlock[];
  secondary: SecondaryNavigationBlock[];
  brandIcon: Reference[];
  brandIconUrl?: string;
  layout?: SiteHeader['layout'];
} & Partial<ClassNameProp>;

const Sidebar: React.FC<MobileMenuProps> = ({
  menuOpen,
  toggleMenu,
  main,
  primary,
  secondary,
  brandIcon,
  brandIconUrl,
  layout
}) => {
  const site = useSelector(select.cms.getSiteId);
  const isSportsSelfExcluded = useSelector(select.account.isSportsSelfExcluded);
  const side = useRef<HTMLDivElement>(null);
  const { discoveryMoreMenu, seoMobileMenu, seoDepth } = useSeoMenu(layout);
  const { showLocalesDropdown } = useCustomHeader();

  useBodyScrollLock(menuOpen, side.current);

  return (
    <>
      <div
        className={classNames(styles.sidebar, {
          [styles.sidebarOpen]: menuOpen
        })}
        ref={side}
      >
        <div className={styles.topMenu}>
          <StarsButton
            variant="text"
            size="small"
            className={styles.closeButton}
            icon={<DismissIcon />}
            onClick={toggleMenu}
          />
          <div className={styles.brandLogo}>
            <BrandIconButton
              brandIcon={brandIcon}
              brandIconUrl={brandIconUrl}
              onClick={toggleMenu}
              inMenu
            />
          </div>
        </div>
        <div className={styles.cmsContent}>
          {site !== Sites.NET && !showLocalesDropdown && (
            <BrandLinks
              className={styles.brandLinks}
              main={main}
              brandsInMenu
              onClick={toggleMenu}
            />
          )}
          <CMSHeaderMenu items={primary} onClick={toggleMenu} />
          {!isSportsSelfExcluded && layout !== 'Play Money' && !showLocalesDropdown && (
            <CMSHeaderMenu items={secondary} onClick={toggleMenu} />
          )}
          {seoMobileMenu && seoDepth && !showLocalesDropdown && (
            <SEOMenu seoMenu={seoMobileMenu} seoDepth={seoDepth} onClick={toggleMenu} />
          )}
          {discoveryMoreMenu && seoDepth && !showLocalesDropdown && (
            <MoreSection seoMenu={discoveryMoreMenu} seoDepth={seoDepth} onClick={toggleMenu} />
          )}
        </div>
      </div>
      <div
        className={classNames(styles.overlay, {
          [styles.overlayOpen]: menuOpen
        })}
        onClick={toggleMenu}
      />
    </>
  );
};

export default Sidebar;
